/* eslint-disable @atlaskit/design-system/no-physical-properties */
import { useState } from "react";

import { css, keyframes } from "@compiled/react";

export type Feature = {
    header: string;
    content: string | React.ReactNode;
};

type Props = {
    header: string;
    features: Feature[];
};

const rowCss = css({
    margin: "0 auto",
    maxWidth: "1200px"
});

const containerCss = css({
    margin: "0 auto",
    padding: "16px",
    paddingBottom: "64px",
    width: "66.66666%"
});

const headerCss = css({
    borderBottom: "2px solid #dfe1e5",
    color: "#253858",
    fontSize: "36px",
    fontWeight: 500,
    letterSpacing: 0,
    margin: 0,
    padding: "24px 16px"
});

const featureContainerCss = css({
    borderBottom: "2px solid #dfe1e5",
    padding: "0 16px",
    button: {
        alignItems: "center",
        appearance: "none",
        background: "none",
        border: 0,
        color: "#0052cc",
        cursor: "pointer",
        display: "flex",
        padding: 0,
        width: "100%",
        svg: {
            height: "30px",
            width: "30px"
        }
    }
});

const fadeInAnimation = keyframes({
    from: {
        opacity: 0
    },
    to: {
        opacity: 1
    }
});

const featureContentCss = css({
    animation: `${fadeInAnimation} 500ms ease`,
    display: "none",
    marginRight: "64px",
    paddingBottom: "24px",
    a: {
        "color": "#0052cc",
        "textDecoration": "underline",
        "&:hover": {
            color: "#0065ff",
            textDecoration: "none"
        }
    },
    p: {
        margin: "0 0 24px"
    }
});

const showContentCss = css({
    display: "block"
});

const featureHeaderCss = css({
    "color": "#253858",
    "flex": 1,
    "fontSize": "1.5rem",
    "fontWeight": 500,
    "letterSpacing": ".3px",
    "lineHeight": 1.33333333,
    "margin": 0,
    "padding": "24px 0",
    "textAlign": "left",
    "button:hover &": {
        color: "#0052cc"
    }
});

const toggleIconCss = css({
    "background": "#0052cc",
    "borderRadius": "50%",
    "height": "24px",
    "transition": "transform .25s linear",
    "width": "24px",

    "&.plus": {
        transform: "rotate(90deg)"
    },

    "&.minus": {
        transform: "rotate(180deg)"
    },

    "&.plus:before, &.plus:after, &.minus:before": {
        borderRadius: "10px",
        content: "",
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "white",
        margin: "auto 5px",
        height: "2px"
    },

    "&.plus:before": {
        height: "auto",
        width: "2px",
        margin: "5px auto"
    }
});

const FaqAccordions = ({ header, features }: Props) => {
    const [expanded, setExpanded] = useState<{
        [dropDownKey: string]: boolean;
    }>({});

    return (
        <div css={rowCss}>
            <div css={containerCss}>
                <h2 css={headerCss}>{header}</h2>
                {features.map((feature) => (
                    <div css={featureContainerCss} key={feature.header}>
                        <button
                            onClick={() =>
                                setExpanded({
                                    ...expanded,
                                    [feature.header]: !expanded[feature.header]
                                })
                            }
                        >
                            <h3 css={featureHeaderCss}>{feature.header}</h3>
                            <div
                                css={toggleIconCss}
                                className={
                                    expanded[feature.header] ? "minus" : "plus"
                                }
                            />
                        </button>
                        <div
                            css={[
                                featureContentCss,
                                expanded[feature.header] && showContentCss
                            ]}
                        >
                            {feature.content}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default FaqAccordions;
